import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PageHero from '../../components/public/PageHero';
import PublicLayout from '../../components/public/PublicLayout';
import { withBase } from '../../publicSite/config';
import { usePageMeta } from '../../publicSite/usePageMeta';
import { addLeadFs } from '../../utils/firestoreStore';

const initialForm = { name: '', surname: '', email: '', phone: '' };

const readSignups = () => {
  try {
    const saved = JSON.parse(localStorage.getItem('shem_event_signups') || '[]');
    return Array.isArray(saved) ? saved : [];
  } catch {
    return [];
  }
};

export default function EventSignup() {
  const location = useLocation();
  const useFirebase = process.env.REACT_APP_USE_FIREBASE === 'true';
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const eventTitle = params.get('title') || 'School of SHEM Event';
  const eventDate = params.get('date') || '';
  const eventId = params.get('eventId') || '';
  const eventImage = params.get('image') || '';
  usePageMeta(`Register: ${eventTitle} | School of Shem`, `Register for ${eventTitle}.`);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const update = (field) => (event) => {
    setForm((previous) => ({ ...previous, [field]: event.target.value }));
    setError('');
    setSuccess(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess(false);

    if (!form.name.trim() || !form.surname.trim() || !form.email.trim() || !form.phone.trim()) {
      setError('Please complete all required fields.');
      return;
    }

    setSubmitting(true);
    const payload = {
      eventId,
      eventTitle,
      eventDate,
      eventImage,
      name: form.name.trim(),
      surname: form.surname.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      submittedAt: new Date().toISOString()
    };

    try {
      localStorage.setItem('shem_event_signups', JSON.stringify([payload, ...readSignups()]));

      if (useFirebase) {
        try {
          await addLeadFs({
            fullName: payload.name,
            surname: payload.surname,
            email: payload.email,
            phone: payload.phone,
            stage: 'event_signup',
            intake: eventTitle,
            eventId,
            eventName: eventTitle,
            eventDate,
            eventImage
          });
        } catch {
          // The email workflow below remains available if public Firestore writes are restricted.
        }
      }

      const response = await fetch('/api/event-signup-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error('email_failed');

      setSuccess(true);
      setForm(initialForm);
    } catch {
      setError('We could not complete the registration right now. Please try again or contact the School of SHEM team.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <PageHero
        eyebrow="Event registration"
        title={eventTitle}
        subtitle={eventDate || 'Complete the registration form to reserve your place.'}
        image="/images/shem-community-event.jpg"
        imageAlt="Guests connecting at a School of SHEM community event"
        actions={<Link to={withBase('/events')} className="btn btn-outline">Back to Events</Link>}
      />

      <section className="ps-section">
        <div className="ps-section-inner public-registration-layout">
          {eventImage && (
            <div className="public-registration-image">
              <img src={eventImage} alt="" />
            </div>
          )}
          <div className="public-enquiry-panel">
            <h2>Register for this event</h2>
            <p>Complete all fields. We will send confirmation to the email address provided.</p>
            {error && <div className="public-form-error" id="event-signup-error" role="alert">{error}</div>}
            {success && (
              <div className="public-form-success" role="status">
                Registration submitted. Please check your email for confirmation.
              </div>
            )}
            <form className="public-form" onSubmit={handleSubmit}>
              <div className="public-form-grid">
                <label>
                  <span>First name</span>
                  <input value={form.name} onChange={update('name')} autoComplete="given-name" aria-describedby={error ? 'event-signup-error' : undefined} required />
                </label>
                <label>
                  <span>Surname</span>
                  <input value={form.surname} onChange={update('surname')} autoComplete="family-name" aria-describedby={error ? 'event-signup-error' : undefined} required />
                </label>
              </div>
              <label>
                <span>Email address</span>
                <input type="email" value={form.email} onChange={update('email')} autoComplete="email" aria-describedby={error ? 'event-signup-error' : undefined} required />
              </label>
              <label>
                <span>Phone number</span>
                <input type="tel" value={form.phone} onChange={update('phone')} autoComplete="tel" aria-describedby={error ? 'event-signup-error' : undefined} required />
              </label>
              <button className="btn btn-primary btn-lg" type="submit" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Register for Event'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
